import {
  formatNavigatorAttrition,
  type summarizeCompletionRows,
  type summarizeNavigatorAttrition,
  type CompletionChannel,
  completionChannelCount,
} from "./careNavigationDefinitionState";
import { CarespaceCountLink } from "./carespaceCountLink";
import type {
  CareNavigationCriteria,
  CareNavigationDefinitionResponse,
} from "./careNavigationCriteria";
import { DataTable, type DataTableColumn } from "./dashboardKit";

type CompletionCounts = ReturnType<typeof summarizeCompletionRows>["current"];
type NavigatorAttrition = ReturnType<typeof summarizeNavigatorAttrition>;
type SummaryRow = CompletionCounts & {
  label: string;
  recordIds?: Record<string, string[]>;
  emailFinalized?: boolean;
  attrition?: { assigned: number; attrited: number } | null;
};
type SummaryMetric = "calls" | "duration" | "carePlan" | "reassessments";
type SummaryBreakdown = "month" | "navigator";

function ratio(numerator: number | null, denominator: number) {
  return denominator && numerator !== null ? numerator / denominator : null;
}

function percentage(value: number | null) {
  return value === null ? "—" : `${(value * 100).toFixed(1)}%`;
}

export function buildCareNavigationSummaryColumns(
  metric: SummaryMetric,
  breakdown: SummaryBreakdown,
  criteria: CareNavigationCriteria,
  channel: CompletionChannel = "calls",
): Array<DataTableColumn<SummaryRow>> {
  const columns: Array<DataTableColumn<SummaryRow>> = [
    {
      key: "label",
      label: breakdown === "month" ? "Month" : "Navigator",
      definition:
        breakdown === "month"
          ? "Reporting month in the dashboard timezone. Counts run through the reporting end date for that month."
          : "Care navigator assigned to the carespaces included in this row for the selected month.",
      sortable: true,
      render: (row) => (breakdown === "month" ? row.label.slice(0, 7) : row.label),
    },
  ];

  if (metric === "calls") {
    columns.push({
      key: "completed",
      sortValue: (row) => completionChannelCount(row, channel),
      render: (row) => (
        <CarespaceCountLink
          count={completionChannelCount(row, channel)}
          preliminary={channel !== "calls" && !row.emailFinalized}
          recordIds={
            channel === "calls"
              ? row.recordIds?.completed
              : channel === "emails"
                ? row.recordIds?.emailCompleted
                : [
                    ...new Set([
                      ...(row.recordIds?.completed ?? []),
                      ...(row.recordIds?.emailCompleted ?? []),
                    ]),
                  ]
          }
          label={`${row.label} · completed`}
        />
      ),
      label:
        channel === "calls"
          ? "Calls completed"
          : channel === "emails"
            ? "Email completions"
            : "Email + calls completed",
      definition:
        channel === "calls"
          ? "Distinct aligned, assigned CareSpaces with a qualifying billed call completion during the reporting month, under the active call-completion criteria. Email completions are shown by selecting Email."
          : channel === "emails"
            ? "Distinct aligned, assigned CareSpaces with a qualifying billed email during the reporting month. Email completion is tracked separately from call criteria and may remain preliminary until reconciliation."
            : "Distinct aligned, assigned CareSpaces with a qualifying billed call completion or email during the reporting month. Each CareSpace counts once, and email completion may remain preliminary until reconciliation.",
      align: "right",
      sortable: true,
    });
    if (breakdown === "navigator") {
      columns.push({
        key: "bookedRemaining",
        render: (row) => (
          <CarespaceCountLink
            count={row.bookedRemaining}
            recordIds={row.recordIds?.bookedRemaining}
            label={`${row.label} · booked CareSpaces`}
          />
        ),
        label: "Booked",
        definition:
          "Distinct aligned carespaces not yet credited with completion that have a qualifying active future booking or expected low-tier group attendance. This is conditional projected completion under the active criteria.",
        align: "right",
        sortable: true,
      });
      columns.push({
        key: "bookedPercentage",
        label: "Booked %",
        definition:
          "Qualifying booked remaining divided by aligned Carespaces for this navigator, under the active call criteria. Each Carespace counts once.",
        align: "right",
        sortable: true,
        sortValue: (row) => ratio(row.bookedRemaining, row.aligned),
        render: (row) => percentage(ratio(row.bookedRemaining, row.aligned)),
      });
    }
    columns.push(
      {
        key: "aligned",
        render: (row) => (
          <CarespaceCountLink
            count={row.aligned}
            recordIds={row.recordIds?.aligned}
            label={`${row.label} · aligned`}
          />
        ),
        label: "Aligned",
        definition:
          "CareSpaces aligned and non-archived at any point in this reporting month, with a currently assigned care navigator. This month-specific cohort is the completion denominator.",
        align: "right",
        sortable: true,
      },
      {
        key: "completion",
        label: "Completion",
        definition:
          "Selected-channel completions divided by CareSpaces aligned in that month. Email + calls uses the distinct union, counting each Carespace once. An em dash means data or the denominator is unavailable.",
        align: "right",
        sortable: true,
        sortValue: (row) => ratio(completionChannelCount(row, channel), row.aligned),
        render: (row) => percentage(ratio(completionChannelCount(row, channel), row.aligned)),
      },
    );
    if (breakdown === "navigator") {
      columns.push({
        key: "attrition",
        label: "Attrition %",
        definition:
          "Distinct currently assigned carespaces offboarded from aligned status during the reporting month, divided by all currently assigned carespaces. Independent of call criteria; older snapshots without attrition data show an em dash.",
        align: "right",
        sortable: true,
        sortValue: (row) =>
          row.attrition ? ratio(row.attrition.attrited, row.attrition.assigned) : null,
        render: (row) => (
          <span
            title={
              row.attrition
                ? `${row.attrition.attrited} / ${row.attrition.assigned} assigned carespaces`
                : undefined
            }
          >
            {formatNavigatorAttrition(row.attrition)}
          </span>
        ),
      });
    }
  } else if (metric === "duration") {
    columns.push(
      {
        key: "average",
        label: "Average",
        definition:
          "Total Dialpad minutes for calls labeled Monthly call or Planned monthly call, divided by distinct calls. Calls are attributed to the resolved Dialpad handler, falling back to the current Carespace assignment when the handler cannot be resolved. A handler-resolved call does not need a Carespace match.",
        align: "right",
        sortable: true,
        sortValue: (row) =>
          row.durationStatus === "available"
            ? ratio(row.callDurationMinutes, row.callDurationCount)
            : null,
        render: (row) => {
          if (row.durationStatus !== "available")
            return row.durationStatus === "loading" ? "Loading…" : "Unavailable";
          const average = ratio(row.callDurationMinutes, row.callDurationCount);
          return average === null ? "—" : `${average.toFixed(2)} min`;
        },
      },
      {
        key: "callDurationCount",
        label: "Monthly calls",
        definition:
          "Distinct Dialpad calls carrying the Monthly call or Planned monthly call disposition. Each call counts once per navigator; team totals deduplicate a call once across all navigators.",
        align: "right",
        sortable: true,
        sortValue: (row) => (row.durationStatus === "available" ? row.callDurationCount : null),
        render: (row) =>
          row.durationStatus === "available"
            ? row.callDurationCount
            : row.durationStatus === "loading"
              ? "Loading…"
              : "Unavailable",
      },
    );
    columns.push({
      key: "needsAssessmentAverage",
      label: "Needs assessment average",
      align: "right",
      sortable: true,
      definition:
        "Verified call minutes divided by matched initial needs assessment calls; annual assessments and unmatched calls are excluded.",
      sortValue: (row) =>
        row.durationStatus === "available"
          ? ratio(row.needsAssessmentDurationMinutes, row.needsAssessmentDurationCount)
          : null,
      render: (row) => {
        if (row.durationStatus !== "available")
          return row.durationStatus === "loading" ? "Loading…" : "Unavailable";
        const average = ratio(row.needsAssessmentDurationMinutes, row.needsAssessmentDurationCount);
        return average === null ? "—" : `${average.toFixed(1)} min`;
      },
    });
  } else if (metric === "carePlan") {
    columns.push(
      {
        key: "carePlanUpdated",
        render: (row) => (
          <CarespaceCountLink
            count={row.carePlanUpdated}
            recordIds={row.recordIds?.carePlanUpdated}
            label={`${row.label} · updated care plans`}
          />
        ),
        label: "Updated",
        definition:
          "Distinct aligned carespaces with a care-plan intervention or goal added, updated, or completed during the reporting month. Credit does not require an encounter. Comments and archiving goals or interventions do not count.",
        align: "right",
        sortable: true,
      },
      {
        key: "carePlanEligible",
        render: (row) => (
          <CarespaceCountLink
            count={row.carePlanEligible}
            recordIds={row.recordIds?.carePlanEligible}
            label={`${row.label} · eligible for care-plan completion`}
          />
        ),
        label: "Eligible",
        definition:
          "Distinct aligned carespaces with either a qualifying care-plan update or a completed encounter/call or eligible Low-tier group attendance during the reporting month. Carespaces with neither are excluded, so they do not count against the navigator. Emails alone do not create a care-plan update requirement.",
        align: "right",
        sortable: true,
      },
      {
        key: "completion",
        label: "Completion",
        definition:
          "Updated care plans divided by eligible carespaces, expressed as a percentage. An em dash means there are no eligible carespaces.",
        align: "right",
        sortable: true,
        sortValue: (row) => ratio(row.carePlanUpdated, row.carePlanEligible),
        render: (row) => percentage(ratio(row.carePlanUpdated, row.carePlanEligible)),
      },
    );
  } else {
    columns.push(
      {
        key: "reassessmentCount",
        render: (row) => (
          <CarespaceCountLink
            count={row.reassessmentCount}
            recordIds={row.recordIds?.reassessmentCount}
            label={`${row.label} · completed reassessments`}
          />
        ),
        label: "Completed",
        definition: `Carespaces in the monthly due cohort with a qualifying ${criteria.pendingReassessments ? "approved, submitted, or pending" : "approved"} annual PAF reassessment completed inside the annual window through the reporting end date. The window spans days 306–425 after the last approved assessment.`,
        align: "right",
        sortable: true,
      },
      {
        key: "reassessmentDenominator",
        render: (row) => (
          <CarespaceCountLink
            count={row.reassessmentDenominator}
            recordIds={row.recordIds?.reassessmentDenominator}
            label={`${row.label} · due reassessments`}
          />
        ),
        label: "Due",
        definition:
          "Aligned carespaces whose annual reassessment window closes during the reporting month, through the reporting end date. This is the reassessment-completion denominator; tracking begins July 2026.",
        align: "right",
        sortable: true,
      },
    );
  }

  return columns;
}

export function CareNavigationSummaryTable(props: {
  metric: SummaryMetric;
  breakdown: SummaryBreakdown;
  criteria: CareNavigationCriteria;
  entries: Array<[string, CompletionCounts]>;
  sourceRows?: CareNavigationDefinitionResponse["rows"];
  attrition?: NavigatorAttrition;
  channel?: CompletionChannel;
  loading?: boolean;
  unavailable?: boolean;
}) {
  if (props.loading) return <p className="mt-2 text-sm">Loading metrics…</p>;
  if (props.unavailable)
    return (
      <p role="alert" className="mt-2 text-sm">
        Metrics are temporarily unavailable.
      </p>
    );
  const rows = props.entries.map(([label, counts]) => {
    const source = props.sourceRows?.filter((row) =>
      props.breakdown === "month" ? row.monthStart === label : row.navigatorName === label,
    );
    const keys = new Set(source?.flatMap((row) => Object.keys(row.recordIds ?? {})));
    const recordIds = Object.fromEntries(
      [...keys].map((key) => [
        key,
        [...new Set(source?.flatMap((row) => row.recordIds?.[key] ?? []))],
      ]),
    );
    return {
      ...counts,
      label,
      attrition: props.attrition?.get(label) ?? null,
      recordIds,
      emailFinalized: Boolean(source?.length && source.every((row) => row.emailFinalized)),
    };
  });
  return (
    <div className="mt-2">
      <DataTable
        columns={buildCareNavigationSummaryColumns(
          props.metric,
          props.breakdown,
          props.criteria,
          props.channel,
        )}
        rows={rows}
        rowKey={(row) => row.label}
      />
    </div>
  );
}
